import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setCookieDetails } from '../data/cookieDetailsSlice';
import { 
  bindingAccount,
  getTransactionResult, 
  updateTransactionStatus,
  getPaymentCredentials 
} from '../libs/GsmpiApi';
import CancelButton from './buttons/CancelButton';
import { 
  TRANSACTION_STATUS, 
  POLLING_TIME
} from '../common/constants';
import { useSelector } from 'react-redux';
import styles from '../css/pc/PcGeneral.module.css';
import '../css/pc/AccountBindingByEmailInputTab.css';
import classNames from 'classnames/bind';
import { RSA, POST_MESSAGE_CONTENT_MESSAGE } from '../common/constants';
import JSEncrypt from 'jsencrypt';
import { getCookieDetails } from '../utils/cookieDetails';
import debug from 'debug';

const cx = classNames.bind(styles);
const log = debug('app:AccountBindingByEmailInputTab');

export default function AccountBindingByEmailInputTab() {
  const emailRef = useRef(null);
  const abortControllersRef = useRef([]);
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [isNoAvailableWallet, setIsNoAvailableWallet] = useState(false);
  const [isNotSupportedCountry, setIsNotSupportedCountry] = useState(false);
  const [isTooltipHidden, setIsTooltipHidden] = useState(false);
  const [statusPollingCountDown, setStatusPollingCountDown] = useState(POLLING_TIME.EMAIL_POLL);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { serviceId, transactionId, domains } = useSelector((state) => state.paymentClient);
  const browserId = useSelector((state) => state.browserDetails.browserId);

  useEffect(() => {
    const abortController = new AbortController();
    abortControllersRef.current.push(abortController);
    const abortSignal = abortController.signal;

    // Countdown that continuously polls the status of the transaction then does redirection based on the polled status
    if (statusPollingCountDown > 0) {
      const pollingInterval = setInterval(() => {
        setStatusPollingCountDown(prevStatusPollingCountDown => prevStatusPollingCountDown > 0 ? prevStatusPollingCountDown - 1.5 : 0);

        getTransactionResult(transactionId, false, abortSignal)
          .then(result => {
            handlePollingResult(result);
          })
          .catch(error => {
            log(error);
            handlePollingError(error);
          });
      }, 1500);

      return () => clearInterval(pollingInterval);
    }
  }, [statusPollingCountDown]);

  useEffect(() => {
    let emailInput = document.getElementById('email');

    const handleInputFocus = () => {
      emailInput.style.border = '2px solid';
      emailInput.style.borderColor = '#387AFF';
      emailInput.style.outline = 'none';
    };

    const handleInputFocusOut = () => {
      emailInput.style.border = '1px solid';
      emailInput.style.borderColor = '#F4F4F7';
      emailInput.style.outline = 'none';
    };

    emailInput.addEventListener('focus', handleInputFocus);
    emailInput.addEventListener('focusout', handleInputFocusOut);

    return () => {
      emailInput.removeEventListener('focus', handleInputFocus);
      emailInput.removeEventListener('focusout', handleInputFocusOut);
    };
  }, []);

  const handlePollingResult = (result) => {
    if (result.data.status === TRANSACTION_STATUS.CONFIRMED) {
      cancelPollingRequests();
      handleConfirmedTransaction(result.data.refId, getCookieDetails(result.headers.get("x-smps-gsmpi-cookie-id")));
      setStatusPollingCountDown(0);
    } else if (result.data.status === TRANSACTION_STATUS.EXPIRED) {
      navigate('/gsmpi-web/timeout', {
        state: {
          timedOutType: POST_MESSAGE_CONTENT_MESSAGE.PAYMENT_TIMEOUT
        }
      });
    } else if (result.data.status === TRANSACTION_STATUS.CANCELED) {
      navigate('/gsmpi-web/cancel');
    } else if (result.data.status === TRANSACTION_STATUS.ERRED) {
      navigate('/gsmpi-web/error');
    } else {
      log(`result status : ${result.data.status}`);
    }
  };

  const handleConfirmedTransaction = (refId, cookieDetails) => {
    getPaymentCredentials({refId: refId, serviceId: serviceId})
      .then((result) => {
        const state = {
          state: {
            paymentCredential: result.data,
            cookie: cookieDetails,
            referenceId: refId
          }
        };

        navigate('/gsmpi-web/done', state);
      })
      .catch(() => navigate('/gsmpi-web/error'));
  };

  const handlePollingError = (error) => {
    if (error.response.data.resultCode === 'OPM2N1101' || error.response.data.resultCode === 'OPM1N1039') {
      //transaction is expired
      navigate('/gsmpi-web/timeout', {
        state: {
          timedOutType: POST_MESSAGE_CONTENT_MESSAGE.PAYMENT_TIMEOUT
        }
      });
    } else if (error.response.data.resultCode === 'OPM2N1103' || error.response.data.resultCode === 'OPM2N1104') {
      //transaction is invalid
      navigate('/gsmpi-web/error');
    } else {
      navigate('/gsmpi-web/error');
    }
  };

  const cancelPollingRequests = () => {
    abortControllersRef.current.forEach(controller => controller.abort());
    abortControllersRef.current = [];
  };

  const handleSetCookieDetails = (cookieValue, hasCookies) => {
    const cookieDetails = {
      cookieValue: cookieValue,
      hasCookies: hasCookies
    };

    dispatch(setCookieDetails(cookieDetails));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const email = emailRef.current.value;

    if (isValidEmail(email)) {
      const jsEncrypt = new JSEncrypt();
      jsEncrypt.setPublicKey(RSA.PUBLIC_KEY);

      let requestWithParams = {
        request: {
          serviceId: serviceId,
          saEmail: jsEncrypt.encrypt(email),
          browserId: browserId,
          domains: domains
        },
        params: {
          transactionId: transactionId
        }
      };
      bindAccountRequest(requestWithParams)
        .then(result => navigate('/gsmpi-web/time-remaining', {state: result}))
        .catch(error => {
          if (error.resultCode === 'OPM1N1040') {
            handleSetCookieDetails(maskUserValue(email), false);
          } else if (error.resultCode === 'OPM2N1102') {  //no phones linked to SamsungPay Account
            setIsEmailInvalid(false);
            setIsNotSupportedCountry(false);
            setIsNoAvailableWallet(true);
            changeInputStyleToError();
          } else if (error.resultCode === 'OPM1N1042') {  //device not supported
            setIsEmailInvalid(false);
            setIsNoAvailableWallet(false);
            setIsNotSupportedCountry(true);
            changeInputStyleToError();
          } else if (error.resultCode === 'OPM2N1101' || error.resultCode === 'OPM1N1039') {  //transaction is expired
            navigate('/gsmpi-web/timeout');
          } else if (error.resultCode === 'OPM2N1103' || error.resultCode === 'OPM2N1104') {  //transaction is invalid
            navigate('/gsmpi-web/error');
          } else if (error.resultCode === 'OPM1N1041') {  //retry count exceeded
            let cancelPayload = {
              payment: {
                status: TRANSACTION_STATUS.EXPIRED,
                reason: 'Exceeded retry count'
              },
              timestamp: new Date().getTime()
            };

            updateTransactionStatus(transactionId, cancelPayload)
              .then(() => {
                navigate('/gsmpi-web/cancel');
              });
          } else {
            navigate('/gsmpi-web/error');
          }
        });
    } else {
      setIsNoAvailableWallet(false);
      setIsNotSupportedCountry(false);
      setIsEmailInvalid(true);
      changeInputStyleToError();
    }
  };

  const maskUserValue = (userEmail) => {
    const userEmailArray = userEmail.split("@");
    const emailLength = userEmailArray[0].length * 2 / 3;
    const regex = new RegExp("(?<=.{" + Math.min(6, emailLength) + "}).", "g");
    const maskedValue = userEmailArray[0].replaceAll(regex, "*");

    return maskedValue + "@" + userEmailArray[1];
  };

  async function bindAccountRequest({request, params}) {

    await bindingAccount(params.transactionId, request)
      .catch((error) => {
        throw error;
      });

    log('done bindingAccount api call');

    return {
      transactionId: params.transactionId,
      email: request.saEmail
    };
  }
  
  function isValidEmail(email) {
    return /^[a-zA-Z0-9._+-]+@\w+([.-]?\w+)*(\.\w{2,})+$/.test(email);
  }

  const changeInputStyleToError = () => {
    let emailInput = document.getElementById('email');
    emailInput.style.border = '2px solid';
    emailInput.style.borderColor = '#D93E36';
  };

  return(
    <>
      <div className={`${cx('mpi-tab')} ${cx('is-clearfix')}`}>
        <div className="mpi-tab-cont" id="enteremail">
          <div className="mpi-wrap-input">
            <input type="text" placeholder="Email address" maxLength="150" className="mpi-input"
              id="email" ref={emailRef}/>
            <input type="hidden" value={transactionId}/>

          </div>
          <div className="mpi-tooltip-blank"></div>

          <p><a className="mpi-findaccount" onClick={() => setIsTooltipHidden(true)}>
            {t('pay.gsmpi.main.tooltip.link')}</a>
          </p>

          {isTooltipHidden && (
            <div className="mpi-tooltip-popup">
              <div className="mpi-tooltip-pop-blank"></div>
              <div className="mpi-tooltip-box">
                <p className="mpi-tooltip-title">{t('pay.gsmpi.main.tooltip.title')}</p>
                <ul className="mpi-tooltip-list">
                  {t('pay.gsmpi.main.tooltip.descriptions').split('<br>').map((line, index) => (
                    <li key={index}><span className="mpi-tooltip-num">- </span>{line}</li>
                  ))}
                </ul>
                <div>
                  <button className="mpi-tooltip-btn" onClick={() => setIsTooltipHidden(false)}>
                    {t('pay.gsmpi.main.tooltip.btn')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="mpi-email-boxbottom">
        <div className="asmpi-errmsg" >
          { isNoAvailableWallet ? <p id="errmsg1">{t('pay.gsmpi.main.errmsg1')}</p> : null }
          { isNotSupportedCountry ? <p id="errmsg2">{t('pay.gsmpi.main.errmsg2')}</p> : null }
          { isEmailInvalid ? <p id="errmsg4">{t('pay.gsmpi.main.errmsg4')}</p> : null }
        </div>
        <div className={`${cx('mpi-button')} ${cx('is-clearfix')}`}>
          <CancelButton className={cx('mpi-button-cancel')} transactionId={transactionId} />
          <button type="button" className={cx('mpi-button-submit')} onClick={handleSubmit}>
            {t('pay.gsmpi.main.button2')}
          </button>
        </div>
      </div>
    </>
  );
}